import { useEffect, useState } from "react";

import Button from "../../../ui/buttons/Button";
import { whatsappService } from "../../../services/whatsapp.service";
import { extractApiErrorMessage } from "../../../utils/apiError";

import styles from "./LeadWhatsApp.module.css";

export const NO_NUMBER_MESSAGE =
  "La cuenta del lead no tiene un número de WhatsApp conectado. Un admin de la cuenta lo conecta en Configuración → Cuentas linkeadas.";
export const NO_PERSONS_MESSAGE =
  "El lead no tiene personas cargadas: agregale un contacto para escribirle por WhatsApp.";
export const NO_PHONE_MESSAGE =
  "La persona no tiene un teléfono cargado: agregalo en su ficha para escribirle.";
export const PHONE_SOURCE_HINT = "Sale de la ficha de la persona.";
export const FIRST_MESSAGE_TEMPLATE_HINT =
  "Como la persona todavía no te escribió, el primer mensaje tiene que ser una plantilla aprobada: al abrir la conversación vas a elegirla.";
export const LOAD_ERROR_MESSAGE =
  "No se pudo preparar la conversación nueva. Probá de nuevo en un momento.";

const connectionLabel = (connection) =>
  [
    connection.display_phone_number || connection.phone_number_id,
    connection.verified_name,
    connection.mail_account_name || connection.mail_account_email,
  ]
    .filter(Boolean)
    .join(" · ");

const personLabel = (person) => {
  if (person.thread_id) return `${person.name} · ya tiene conversación`;
  if (!person.phones.length) return `${person.name} · sin teléfono`;
  return person.name;
};

const WhatsAppNewThreadForm = ({
  accountId = null,
  responsibleMailAccountId = null,
  leadId,
  creating = false,
  onCreate,
  onCancel,
  onOpenExisting,
}) => {
  const [connections, setConnections] = useState([]);
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [connectionId, setConnectionId] = useState("");
  const [personId, setPersonId] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setLoadError(null);
    Promise.all([
      whatsappService.listAccounts(),
      whatsappService.listLeadPersons(leadId),
    ])
      .then(([{ results }, candidates]) => {
        if (!mounted) return;
        const own = accountId
          ? results.filter((connection) => Number(connection.account) === Number(accountId))
          : results;
        setConnections(own);
        const preferred =
          own.find((connection) => Number(connection.mail_account) === Number(responsibleMailAccountId)) ??
          own[0];
        setConnectionId(preferred?.id ? String(preferred.id) : "");
        setPersons(candidates);
        if (candidates.length === 1) {
          setPersonId(String(candidates[0].person_id));
          setPhone(candidates[0].phones[0] || "");
        }
      })
      .catch((requestError) => {
        if (!mounted) return;
        setLoadError(extractApiErrorMessage(requestError) || LOAD_ERROR_MESSAGE);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [accountId, leadId, reloadKey, responsibleMailAccountId]);

  const selectedPerson =
    persons.find((person) => String(person.person_id) === personId) ?? null;

  const handlePersonChange = (event) => {
    const value = event.target.value;
    setPersonId(value);
    setError(null);
    const person = persons.find((candidate) => String(candidate.person_id) === value);
    setPhone(person?.phones[0] || "");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (creating || !selectedPerson) return;
    if (selectedPerson.thread_id) {
      onOpenExisting?.(selectedPerson.thread_id);
      return;
    }
    if (!connectionId) {
      setError(NO_NUMBER_MESSAGE);
      return;
    }
    if (!selectedPerson.phones.length) {
      setError(NO_PHONE_MESSAGE);
      return;
    }
    setError(null);
    try {
      await onCreate?.({
        whatsappAccountId: Number(connectionId),
        personId: Number(selectedPerson.person_id),
        phone: phone || selectedPerson.phones[0],
      });
    } catch (requestError) {
      setError(
        extractApiErrorMessage(requestError) || "No se pudo abrir la conversación.",
      );
    }
  };

  const submitLabel = selectedPerson?.thread_id
    ? "Ir a la conversación"
    : "Abrir conversación";
  const canSubmit =
    !loading &&
    selectedPerson != null &&
    (Boolean(selectedPerson.thread_id) ||
      (connections.length > 0 && selectedPerson.phones.length > 0));

  return (
    <section className={styles.conversationPanel}>
      <header className={styles.conversationHeader}>
        <div className={styles.conversationHeaderContent}>
          <h3 className={styles.conversationTitle}>Nueva conversación</h3>
          <p className={styles.conversationSubtitle}>
            Elegí a qué persona del lead escribirle por WhatsApp.
          </p>
        </div>
      </header>

      <form className={styles.newThreadForm} onSubmit={handleSubmit}>
        {loading ? (
          <p className={styles.formHint}>Cargando personas y números...</p>
        ) : loadError ? (
          <>
            <p className={styles.formNotice} role="alert">
              {loadError}
            </p>
            <div className={styles.formActions}>
              <Button variant="secondary" tone="neutral" onClick={onCancel}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                tone="green"
                onClick={() => setReloadKey((key) => key + 1)}
              >
                Reintentar
              </Button>
            </div>
          </>
        ) : (
          <>
            {connections.length === 0 && (
              <p className={styles.formNotice} role="status">
                {NO_NUMBER_MESSAGE}
              </p>
            )}
            {connections.length > 1 && (
              <label className={styles.formField}>
                <span className={styles.formLabel}>Número</span>
                <select
                  className={styles.formSelect}
                  value={connectionId}
                  onChange={(event) => setConnectionId(event.target.value)}
                >
                  {connections.map((connection) => (
                    <option key={connection.id} value={connection.id}>
                      {connectionLabel(connection)}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {persons.length === 0 ? (
              <p className={styles.formNotice} role="status">
                {NO_PERSONS_MESSAGE}
              </p>
            ) : (
              <label className={styles.formField}>
                <span className={styles.formLabel}>Persona del lead</span>
                <select
                  className={styles.formSelect}
                  value={personId}
                  onChange={handlePersonChange}
                  aria-label="Persona del lead"
                >
                  <option value="" disabled>
                    Elegí una persona
                  </option>
                  {persons.map((person) => (
                    <option key={person.person_id} value={person.person_id}>
                      {personLabel(person)}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {selectedPerson && !selectedPerson.thread_id && (
              <div className={styles.formField}>
                <span className={styles.formLabel}>Teléfono</span>
                {selectedPerson.phones.length > 1 ? (
                  <select
                    className={styles.formSelect}
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    aria-label="Teléfono"
                  >
                    {selectedPerson.phones.map((personPhone) => (
                      <option key={personPhone} value={personPhone}>
                        {personPhone}
                      </option>
                    ))}
                  </select>
                ) : selectedPerson.phones.length === 1 ? (
                  <>
                    <span className={styles.formValue} aria-label="Teléfono">
                      {selectedPerson.phones[0]}
                    </span>
                    <span className={styles.formHint}>{PHONE_SOURCE_HINT}</span>
                  </>
                ) : (
                  <span className={styles.formHint}>{NO_PHONE_MESSAGE}</span>
                )}
              </div>
            )}
          </>
        )}

        {selectedPerson && !selectedPerson.thread_id && selectedPerson.phones.length > 0 && (
          <p className={styles.formHint}>
            {FIRST_MESSAGE_TEMPLATE_HINT}
          </p>
        )}

        {error && (
          <p className={styles.composerError} role="alert">
            {error}
          </p>
        )}

        {!loadError && (
          <div className={styles.formActions}>
            <Button variant="secondary" tone="neutral" onClick={onCancel} disabled={creating}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              tone="green"
              loading={creating}
              disabled={!canSubmit}
            >
              {submitLabel}
            </Button>
          </div>
        )}
      </form>
    </section>
  );
};

export default WhatsAppNewThreadForm;
